import React, { useMemo } from 'react';
import { Form, Popover } from 'antd';
import { columns, PlanogramWithChanges } from './columns';
import {
    PlanogramUI,
    usePlanogramAutomationContext,
} from '../../context/PlanogramAutomation';
import * as S from './styled';
import { PriceCalcOptions } from '../PlanogramAdjust';
import { hasDataToCalculatePrice } from '../Modal/utils';
import {
    HeaderPlanogramAutomation,
    HeaderPlanogramAutomationArea,
} from '../Header';

export type PlanogramUIWithChanges = PlanogramUI & {
    enabled_to_calculate_margin?: boolean;
    price_calc_option: keyof typeof PriceCalcOptions;
};

const popoverMessage = (
    <>
        <strong>Margem indisponível</strong>
        <br />
        A categoria ou produto não possui todas as informações necessárias para
        o cálculo.
        <br />
        Os valores não sofrerão alterações.
    </>
);

export const PlanogramReview = () => {
    const { planogramChecked, planogramsUpdated } =
        usePlanogramAutomationContext();

    const form = Form.useFormInstance();

    const price_calc_option = Form.useWatch('price_calc_option', form);

    const dataSource = useMemo(() => {
        return planogramChecked.map((item) => {
            const updated = planogramsUpdated.find((u) => u.id === item.id);

            const hasDataToCalculatePriceListAndPriceApp =
                hasDataToCalculatePrice(item);
            return {
                ...item,
                price_final_in_cents_after:
                    updated?.price_final_in_cents_after ??
                    item.price_final_in_cents,
                price_list_in_cents_after:
                    updated?.price_list_in_cents_after ??
                    item.price_list_in_cents,
                status_after: updated?.status ?? item.status,
                alert_level_after: updated?.alert_level ?? item.alert_level,
                alert_level: item.alert_level,
                standard_amount_after: updated?.standard_amount,
                enabled_to_calculate_margin:
                    hasDataToCalculatePriceListAndPriceApp && !updated?.delete,
                price_calc_option,
                delete: updated?.delete,
                should_remove_line: updated?.should_remove_line,
            };
        });
    }, [planogramsUpdated, planogramChecked]);

    const orderDataSourceFromShouldRemoveLine = useMemo(() => {
        const removedLines = dataSource.filter(
            (item) => item.should_remove_line,
        );
        const otherLines = dataSource.filter(
            (item) => !item.should_remove_line,
        );

        return [...otherLines, ...removedLines];
    }, [dataSource]);

    return (
        <S.ContainerArea>
            <HeaderPlanogramAutomationArea style={{ padding: 0 }}>
                <HeaderPlanogramAutomation />
            </HeaderPlanogramAutomationArea>
            <S.TableWrapeer>
                <S.StyledTable
                    columns={columns}
                    dataSource={orderDataSourceFromShouldRemoveLine}
                    pagination={false}
                    bordered
                    components={{
                        body: {
                            row: (props: any) => {
                                const { className, ...rest } = props;
                                const record: number = props['data-row-key'];

                                const findLine = dataSource.find(
                                    (p) => p.id === record,
                                ) as unknown as
                                    | PlanogramWithChanges
                                    | undefined;

                                const isInvalid =
                                    !findLine?.enabled_to_calculate_margin &&
                                    findLine?.price_calc_option ===
                                        PriceCalcOptions.categoryMargin &&
                                    !findLine?.delete;

                                if (!isInvalid) {
                                    return (
                                        <tr
                                            {...rest}
                                            className={
                                                findLine?.delete
                                                    ? 'row-delete'
                                                    : ''
                                            }
                                        />
                                    );
                                }

                                return (
                                    <Popover
                                        content={popoverMessage}
                                        trigger="hover"
                                        align={{ offset: [0, 30] }}
                                    >
                                        <tr
                                            {...rest}
                                            className={`${className} ${
                                                isInvalid ? 'row-invalid' : ''
                                            } ${
                                                findLine?.delete
                                                    ? 'row-delete'
                                                    : ''
                                            }`}
                                        />
                                    </Popover>
                                );
                            },
                        },
                    }}
                    size="middle"
                    tableLayout="fixed"
                    scroll={{ x: 'max-content' }}
                />
            </S.TableWrapeer>
        </S.ContainerArea>
    );
};
